/* dashboard/graphs.js — pure data shaping for the 8 dashboard graphs.
 *
 * Extracted from static/js/live.js on 2026-05-31 (Phase L1 of the
 * live.js refactor, see memory [[user-dashboards-architecture]]). The
 * old getters inside dashboard() are now thin delegations of the form
 *   get graphBarChartHtml() { return LSGraphs.barChart(this); }
 * The template-side API is unchanged — widgets still read
 * dashboard.graphBarChartHtml / dashboard.graphPaceData / etc.
 *
 * Why extract: ~350 lines of pure data → SVG / object transformation
 * sat inside the controller, making it harder to scan and impossible
 * to test in isolation. Each function here takes a `ctx` (the
 * dashboard Alpine controller) and reads from it via the Alpine
 * prototype chain. No mutable state lives in this module — every
 * function is a pure reduction of ctx.lineupRows / ctx.allGames.
 *
 * Public API (all functions take a single `ctx` arg):
 *   barChart(ctx)            -> SVG string (Lineup Scores bar chart)
 *   paceData(ctx)            -> [{id, name, scorePct, ...}, ...]
 *   heatmap(ctx)             -> {players, lineups, heatColor, peak}
 *   contributions(ctx)       -> {players, grand, peak}
 *   lineupLeaderboard(ctx)   -> [{id, name, color, live, proj, ...}, ...]
 *   positionBreakdown(ctx)   -> {rows, grand, peak}
 *   stackPerformance(ctx)    -> {rows, peak}
 *   gameExposure(ctx)        -> {rows, myTotal}
 *
 * ctx fields consumed:
 *   ctx.lineupRows           [{id, lineup_name, live_total, proj_total,
 *                              paced_proj, players: [...]}, ...]
 *   ctx.allGames             [{game_id, home_team, away_team,
 *                              home_score, away_score, status_text,
 *                              status}, ...]
 *   ctx.graphLineupColor(i)  -> '#hex' palette lookup (kept on
 *                               controller because templates also
 *                               call it directly)
 *   ctx.effStatus(g)         -> 1|2|3 (provider flicker-resilient)
 */

window.LSGraphs = (function () {

  /** Lineup Scores bar chart (SVG string).
   *  MME cap (Xav 2026-05-29): at 150 lineups the bars become illegible
   *  3px-wide dashes. Cap to the top 25 by live total — the rest of
   *  the long tail belongs in the Leaderboard chart below, which
   *  shows top 10 + bottom 5 + a "show all" toggle. A small overflow
   *  foot text tells the user when the cap kicked in. */
  function barChart(ctx) {
    const _allLus    = ctx.lineupRows || [];
    const _BAR_CAP   = 25;
    const _capActive = _allLus.length > _BAR_CAP;
    const lus = _capActive
      ? [..._allLus].sort((a, b) => (b.live_total || 0) - (a.live_total || 0)).slice(0, _BAR_CAP)
      : _allLus;
    if (!lus.length) return '';
    const PL=40, PR=10, PT=10, PB=30, W=580, H=200;
    const cW=W-PL-PR, cH=H-PT-PB;
    const maxY = Math.max(...lus.map(l => Math.max(l.live_total||0, l.proj_total||0, 1))) * 1.1;
    const slot = cW / lus.length;
    const barW = Math.max(8, Math.min(48, slot * 0.55));
    const sy = v => PT + cH - (v / maxY) * cH;
    let s = `<svg viewBox="0 0 ${W} ${H}" style="width:100%;max-height:220px">`;
    // y grid lines
    [0, 0.25, 0.5, 0.75, 1].forEach(f => {
      const y = sy(f * maxY).toFixed(1);
      const v = Math.round(f * maxY);
      s += `<line x1="${PL}" y1="${y}" x2="${W-PR}" y2="${y}" stroke="#e2e8f0" stroke-width="1" class="dark-grid"/>`;
      s += `<text x="${PL-4}" y="${(parseFloat(y)+4).toFixed(1)}" fill="#94a3b8" font-size="10" text-anchor="end">${v}</text>`;
    });
    lus.forEach((lu, i) => {
      const cx = PL + slot*i + slot/2;
      const liveH = Math.max((lu.live_total||0) / maxY * cH, 0);
      const color = ctx.graphLineupColor(i);
      const liveX = (cx - barW/2).toFixed(1);
      const liveY = sy(lu.live_total||0).toFixed(1);
      // Dashed line = sum(proj × elapsed_frac) per player — expected score at this point in game time
      const expectedNow = (lu.players || []).reduce((s, p) => s + (p.proj || 0) * (p.elapsed_frac || 0), 0);
      const projY = sy(expectedNow).toFixed(1);
      const labelY = (H - PB + 14).toFixed(1);
      const scoreY = Math.max(sy(lu.live_total||0) - 4, PT + 4).toFixed(1);
      s += `<rect x="${liveX}" y="${liveY}" width="${barW.toFixed(1)}" height="${Math.max(liveH,0).toFixed(1)}" fill="${color}" fill-opacity="0.85" rx="2"/>`;
      s += `<line x1="${(cx-barW/2-3).toFixed(1)}" y1="${projY}" x2="${(cx+barW/2+3).toFixed(1)}" y2="${projY}" stroke="#64748b" stroke-width="1.5" stroke-dasharray="3,2"/>`;
      if (liveH > 12) s += `<text x="${cx.toFixed(1)}" y="${scoreY}" fill="white" font-size="9" text-anchor="middle" font-weight="bold">${(lu.live_total||0).toFixed(2)}</text>`;
      const label = (lu.lineup_name || 'L'+(i+1)).substring(0, 8);
      s += `<text x="${cx.toFixed(1)}" y="${labelY}" fill="#94a3b8" font-size="9" text-anchor="middle">${label}</text>`;
    });
    s += '</svg>';
    if (_capActive) {
      s += `<div class="text-[10px] text-gray-500 dark:text-slate-600 mt-1 text-center">`
         + `Showing top ${_BAR_CAP} of ${_allLus.length} lineups — full ranking in the Leaderboard below.`
         + `</div>`;
    }
    return s;
  }

  /** Pace Tracker — one row per lineup with score% / time% / ahead-behind tags. */
  function paceData(ctx) {
    return (ctx.lineupRows || []).map((lu, i) => {
      // Expected score at this point = sum(proj × elapsed_frac) per player
      const expectedNow = (lu.players || []).reduce((s, p) => s + (p.proj || 0) * (p.elapsed_frac || 0), 0);
      const live = lu.live_total || 0;
      // Scale bar against proj_total so 100% = full projection achieved
      const maxVal = Math.max(lu.proj_total || 0, live, expectedNow, 1);
      const scorePct = Math.min(live        / maxVal * 100, 100);
      const timePct  = Math.min(expectedNow / maxVal * 100, 100);
      const diff = live - expectedNow;
      return {
        id: lu.id,
        name:        lu.lineup_name || 'Lineup ' + (i + 1),
        scorePct,
        timePct,
        ahead:       diff >  2,
        behind:      diff < -2,
        color:       ctx.graphLineupColor(i),
        live:        live.toFixed(2),
        expectedNow: expectedNow.toFixed(2),
        proj:        (lu.proj_total || 0).toFixed(2),
      };
    });
  }

  /** Player Heat Map — players × lineups grid.
   *  Returns { players: [{name, team, peak, total, cells: [n|null,...]}],
   *            lineups: [{lineup_name}],
   *            heatColor(value, peakInRow) -> css color }.
   *  cells[i] is the player's live_fpts in lineup i, or null if they
   *  aren't in that lineup. Rows sorted by total live_fpts desc so the
   *  hottest names sit at the top — MME users scan from there. */
  function heatmap(ctx) {
    const lus = (ctx.lineupRows || []);
    if (!lus.length) return { players: [], lineups: [], heatColor: () => 'transparent', peak: 0 };
    // Build the master player roster: one row per UNIQUE player across
    // all lineups, key by player_id (fall back to name|team).
    const idx = new Map();                  // key -> { name, team, cells, total, peak }
    lus.forEach((lu, li) => {
      for (const p of (lu.players || [])) {
        const k = String(p.player_id || p.external_player_id || '');   // by id, never by name (audit v2 W1)
        if (!k) continue;
        if (!idx.has(k)) {
          idx.set(k, {
            player_id: p.player_id,
            name:      p.name,
            team:      p.team || '',
            cells:     new Array(lus.length).fill(null),
            total:     0,
            peak:      0,
          });
        }
        const row = idx.get(k);
        const v = (p.live_fpts || 0);
        row.cells[li] = v;
        row.total += v;
        if (v > row.peak) row.peak = v;
      }
    });
    const players = [...idx.values()].sort((a, b) => b.total - a.total);
    const lineups = lus.map((lu, li) => ({
      lineup_name: lu.lineup_name || ('Lineup ' + (li + 1)),
    }));
    // GLOBAL peak across every cell — per-row normalisation was a bug
    // (Xav 2026-05-29). With one lineup any player's single cell IS the
    // row peak, so t=1 for every non-zero cell and the whole grid came
    // out one shade. A heat map needs absolute scoring strength as the
    // axis, so the peak is the matrix-wide max.
    let globalPeak = 0;
    for (const row of players) {
      if (row.peak > globalPeak) globalPeak = row.peak;
    }
    // Cold (low ratio) → faint emerald-100. Warm (mid) → vibrant
    // emerald-400 / 500. Hot (peak) → saturated emerald-600 with high
    // alpha so it genuinely pops against the table chrome. The second
    // arg is kept for backward-compat with templates that still pass
    // row.peak; it's just ignored.
    const heatColor = (v, _rowPeakIgnored) => {
      if (v == null)                       return 'transparent';
      if (globalPeak <= 0 || v <= 0)       return 'rgba(148, 163, 184, 0.05)';
      const t = Math.min(1, v / globalPeak);
      const r = Math.round(210 - (210 -   5) * t);
      const g = Math.round(250 - (250 - 150) * t);
      const b = Math.round(230 - (230 - 105) * t);
      const a = 0.20 + 0.75 * Math.pow(t, 0.85);
      return 'rgba(' + r + ', ' + g + ', ' + b + ', ' + a.toFixed(2) + ')';
    };
    return { players, lineups, heatColor, peak: globalPeak };
  }

  /** Player Contributions (aggregated) — one row per UNIQUE player
   *  across all your lineups. Each row shows their TOTAL live_fpts
   *  contribution to your roster, their share of your overall total,
   *  and how many lineups they appear in. This replaces the per-lineup
   *  view that broke at MME scale (Xav 2026-05-29) — at 150 lineups
   *  the per-lineup chart was ~1350 stacked bars over many viewport
   *  heights.
   *
   *  Returns { players: [{ name, team, pos, total, share, lineupCount }],
   *            grand:  number,  // total live FPts across all your players
   *            peak:   number }. Players sorted by total desc. */
  function contributions(ctx) {
    const idx = new Map();   // key -> { name, team, pos, total, lineupCount }
    let grand = 0;
    for (const lu of (ctx.lineupRows || [])) {
      const luSeen = new Set();
      for (const p of (lu.players || [])) {
        const k = String(p.player_id || p.external_player_id || '');   // by id, never by name (audit v2 W1)
        if (!k) continue;
        // Dedupe per-lineup (in case a player slot appears twice).
        if (luSeen.has(k)) continue;
        luSeen.add(k);
        if (!idx.has(k)) {
          idx.set(k, {
            player_id:    p.player_id,
            name:         p.name,
            team:         p.team || '',
            pos:          p.pos || '',
            total:        0,
            lineupCount:  0,
          });
        }
        const row = idx.get(k);
        // ONLY add live_fpts once per unique player — the player scored
        // the same in every lineup they're in, so we sum their unique
        // contribution to YOUR roster, not "fpts × lineup count".
        if (row.lineupCount === 0) {
          row.total = (p.live_fpts || 0);
          grand += (p.live_fpts || 0);
        }
        row.lineupCount += 1;
      }
    }
    const players = [...idx.values()].sort((a, b) => b.total - a.total);
    const peak = players.length ? players[0].total : 0;
    // Stamp share per row now that grand is final.
    for (const r of players) {
      r.share = grand > 0 ? (r.total / grand * 100) : 0;
    }
    return { players, grand, peak };
  }

  /** Lineup Leaderboard — sorted vertical ranking of your lineups by
   *  live_total desc. Each row carries the data needed to render a
   *  progress bar (live vs proj), the paced delta, and an ahead/behind
   *  tag. Pure transformation of lineupRows. */
  function lineupLeaderboard(ctx) {
    const rows = (ctx.lineupRows || []).map((lu, i) => {
      const live  = lu.live_total || 0;
      const proj  = lu.proj_total || 0;
      const paced = lu.paced_proj || 0;
      const diff  = paced > 0 ? live - paced : 0;
      return {
        id:      lu.id || i,
        name:    lu.lineup_name || ('Lineup ' + (i + 1)),
        color:   ctx.graphLineupColor(i),
        live,
        proj,
        paced,
        diff,
        ahead:   diff >  2,
        behind:  diff < -2,
        fillPct: proj > 0 ? Math.min(100, (live / proj) * 100) : 0,
      };
    });
    return rows.sort((a, b) => b.live - a.live);
  }

  /** Position Breakdown — total live FPts grouped by position across
   *  every player in every lineup (deduped by player_id). Tells MME
   *  users where their points are concentrated (and where slack is).
   *  Sorts by total_fpts desc so the biggest sources lead. */
  function positionBreakdown(ctx) {
    const seen   = new Set();
    const byPos  = new Map();   // pos -> { total, count }
    let grand    = 0;
    for (const lu of (ctx.lineupRows || [])) {
      for (const p of (lu.players || [])) {
        // Players in MULTIPLE lineups count their FPts ONCE — the chart
        // shows roster-level signal, not lineup-weighted.
        const k = String(p.player_id || p.external_player_id || '');   // by id, never by name (audit v2 W1)
        if (!k) continue;
        if (seen.has(k)) continue;
        seen.add(k);
        // Normalise multi-position eligibility (e.g. "OF/SS") to first.
        const posRaw = (p.pos || '—').toString();
        const pos    = posRaw.split('/')[0].trim().toUpperCase() || '—';
        if (!byPos.has(pos)) byPos.set(pos, { total: 0, count: 0 });
        const slot = byPos.get(pos);
        slot.total += (p.live_fpts || 0);
        slot.count += 1;
        grand += (p.live_fpts || 0);
      }
    }
    const rows = [...byPos.entries()].map(([pos, v]) => ({
      pos,
      total: v.total,
      count: v.count,
      pct:   grand > 0 ? (v.total / grand) * 100 : 0,
    })).sort((a, b) => b.total - a.total);
    const peak = rows.length ? rows[0].total : 0;
    return { rows, grand, peak };
  }

  /** Stack Performance — group your hitters by team. Every team with
   *  2+ rostered hitters across your lineups (deduped by player_id)
   *  counts as a stack. Combined FPts + "how many have batted" tell
   *  the user whether the stack is live or asleep. MLB-shaped.
   *
   *  A "batted" hitter is one whose game_phase is anything other than
   *  'not_started' — they've at least had a plate appearance scheduled.
   *  Cheaper proxy than parsing per-AB events. */
  function stackPerformance(ctx) {
    const seen     = new Set();
    const byTeam   = new Map();    // team -> { players: [], total, batted }
    const HITTER_POS = new Set(['C','1B','2B','3B','SS','OF','IF','UT','DH','LF','CF','RF','MI','CI']);
    for (const lu of (ctx.lineupRows || [])) {
      for (const p of (lu.players || [])) {
        const k = String(p.player_id || p.external_player_id || '');   // by id, never by name (audit v2 W1)
        if (!k) continue;
        if (seen.has(k)) continue;
        seen.add(k);
        const pos = (p.pos || '').toString().split('/')[0].trim().toUpperCase();
        if (!pos || !HITTER_POS.has(pos)) continue;
        const team = (p.team || '').toUpperCase();
        if (!team) continue;
        if (!byTeam.has(team)) byTeam.set(team, { players: [], total: 0, batted: 0 });
        const stack = byTeam.get(team);
        stack.players.push(p);
        stack.total += (p.live_fpts || 0);
        if (p.game_phase && p.game_phase !== 'not_started') stack.batted += 1;
      }
    }
    const rows = [...byTeam.entries()]
      .filter(([_, v]) => v.players.length >= 2)
      .map(([team, v]) => ({
        team,
        total:        v.total,
        size:         v.players.length,
        batted:       v.batted,
        players:      v.players,
        topScorer:    [...v.players].sort((a, b) => (b.live_fpts || 0) - (a.live_fpts || 0))[0],
      }))
      .sort((a, b) => b.total - a.total);
    const peak = rows.length ? rows[0].total : 0;
    return { rows, peak };
  }

  /** Game State vs Exposure — for every game on the slate that has
   *  any of your players in it, show your exposure (% of your live
   *  points coming from that game) next to the game's score + state.
   *  The "which live game matters most to me right now?" view. */
  function gameExposure(ctx) {
    const games = ctx.allGames || [];
    if (!games.length) return { rows: [], myTotal: 0 };
    // Build player → game_id map via team membership on the game, keyed by
    // team_id. Keyed by ABBREV it silently lost every team the live feed and
    // the DFS platform spell differently (WSH/WAS, JAC/JAX) - those players
    // fell out of Game Exposure entirely rather than showing a wrong number.
    // Keyed id-first, label as the fallback (see live.js _teamKey). Keyed by
    // ABBREV alone it lost every team the live feed and the DFS platform spell
    // differently (WSH/WAS) - those players fell out of Game Exposure entirely.
    // Keyed by team_id alone it would lose the teams that have no team_id yet
    // (130 platform_teams rows, mostly soccer). Both keys, so neither happens.
    const key = (x, side) => {
      const tid = side ? x[side + '_team_id'] : x.team_id;
      if (tid != null) return 'id:' + tid;
      const ab = (side ? x[side + '_team'] : x.team) || '';
      return ab ? 'ab:' + ab.toUpperCase() : '';
    };
    const teamGameId = new Map();
    for (const g of games) {
      for (const side of ['home', 'away']) {
        const k = key(g, side);
        if (k) teamGameId.set(k, g.game_id);
      }
    }
    const byGame = new Map();   // game_id -> { my_fpts, my_count }
    const seen   = new Set();
    let myTotal  = 0;
    for (const lu of (ctx.lineupRows || [])) {
      for (const p of (lu.players || [])) {
        const k = String(p.player_id || p.external_player_id || '');   // by id, never by name (audit v2 W1)
        if (!k) continue;
        if (seen.has(k)) continue;
        seen.add(k);
        const gid = teamGameId.get(key(p));
        if (!gid) continue;
        if (!byGame.has(gid)) byGame.set(gid, { my_fpts: 0, my_count: 0 });
        const slot = byGame.get(gid);
        slot.my_fpts += (p.live_fpts || 0);
        slot.my_count += 1;
        myTotal += (p.live_fpts || 0);
      }
    }
    const rows = games
      .filter(g => byGame.has(g.game_id))
      .map(g => {
        const s = byGame.get(g.game_id);
        return {
          game_id:    g.game_id,
          home:       g.home_team,
          away:       g.away_team,
          home_score: g.home_score ?? 0,
          away_score: g.away_score ?? 0,
          status:     ctx.effStatus(g),
          status_text: g.status_text || '',
          my_fpts:    s.my_fpts,
          my_count:   s.my_count,
          pct:        myTotal > 0 ? (s.my_fpts / myTotal) * 100 : 0,
        };
      })
      .sort((a, b) => b.pct - a.pct);
    return { rows, myTotal };
  }

  return {
    barChart,
    paceData,
    heatmap,
    contributions,
    lineupLeaderboard,
    positionBreakdown,
    stackPerformance,
    gameExposure,
  };
})();
